import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FormControl, Grid, TextField } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import makeStyles from '@mui/styles/makeStyles';
import React from 'react';
import styled from 'styled-components';
import {
	ExposureConfigurationResponse,
	FieldConfiguration,
	PerilConfigurationResponse,
} from '../../../api/ConfigApi';
import { AuthData, HIDE_PERIL_LIMITS_ENABLE } from '../../../context/auth';
import { PolicyData, withPolicyContext } from '../../../context/policy';
import {
	Peril,
	PerilUpdateRequest,
	PolicyCharacteristics,
} from '../../../types/policy';
import FieldLabel from '../../fields/FieldLabel';
import { filterForPolicySnapshot } from '../util_sharedEndorsementAndRenewal';
import FieldGroupChanges from './FieldGroupChanges';
import UpdatedPerilDetails from './UpdatedPerilDetails';

interface Props {
	authData: AuthData;
	perils: Peril[];
	updatedPerils: PerilUpdateRequest[];
	exposureConfiguration: ExposureConfigurationResponse;
	policyCharacteristic: PolicyCharacteristics;
	policyData: PolicyData;
	referenceTimestamp: string;
	characteristicTimestamp: string;
}

const useStyles = makeStyles((theme) => ({
	disabledInput: {
		'& .MuiInputBase-root.Mui-disabled': {
			color: 'rgba(0, 0, 0, 0.54)',
		},
	},
}));

const UpdatedPerils = (props: Props) => {
	const classes = useStyles();
	const {
		perils,
		updatedPerils,
		exposureConfiguration,
		policyCharacteristic,
		policyData,
		authData,
		referenceTimestamp,
		characteristicTimestamp,
	} = props;
	const hidePerilLimitsEnabled =
		authData?.auth?.configProperties?.[HIDE_PERIL_LIMITS_ENABLE] ?? false;

	if (!updatedPerils.length) {
		return null;
	}

	const renderField = (changedFieldValues: any, field: any, index: number) => {
		const value: any = changedFieldValues[field.name] || '';
		return (
			<FormControl>
				<FieldLabel required={!field.optional} title={field.title} />
				<TextField
					className={classes.disabledInput}
					id={`${field.name}-${index}`}
					value={value}
					disabled
				/>
			</FormControl>
		);
	};

	const renderUpdatedFields = (
		changedFieldValues: any,
		fieldConfigurations: FieldConfiguration[],
	) => {
		const changedValues = Object.keys(changedFieldValues).map(
			(fieldName: string, index: number) => {
				const fieldConfig = fieldConfigurations.find(
					(f) => f.name === fieldName,
				);

				const key1 = `${fieldName}-${index}`;

				return (
					<Grid item key={key1}>
						{fieldConfig.newline && <Break />}
						{renderField(changedFieldValues, fieldConfig, index)}
					</Grid>
				);
			},
		);
		if (!changedValues || !changedValues.length) {
			return null;
		}
		return (
			<div style={{ marginTop: '10px ', marginBottom: '10px' }}>
				<Grid container spacing={1}>
					{changedValues}
				</Grid>
			</div>
		);
	};

	const renderFields = (
		updatedPeril: PerilUpdateRequest,
		peril: Peril,
		perilConfig: PerilConfigurationResponse,
	) => {
		const changedFieldValues = updatedPeril.fieldValues || {};
		const fieldConfigs = perilConfig.fields;
		const perilChar = peril.characteristics
			.filter((c) =>
				filterForPolicySnapshot(
					c.createdTimestamp,
					c.replacedTimestamp,
					referenceTimestamp,
				),
			)
			.find(
				(char) =>
					parseInt(char.coverageStartTimestamp, 10) <=
						parseInt(characteristicTimestamp, 10) &&
					parseInt(char.coverageEndTimestamp, 10) >=
						parseInt(characteristicTimestamp, 10),
			);
		return (
			<>
				{renderUpdatedFields(changedFieldValues, fieldConfigs)}
				<FieldGroupChanges
					originalFieldGroups={perilChar.fieldGroupsByLocator}
					originalFieldValues={perilChar.fieldValues}
					addedFieldGroups={updatedPeril.addFieldGroups}
					removedFieldGroups={updatedPeril.removeFieldGroups}
					updatedFieldGroups={updatedPeril.updateFieldGroups}
					fieldConfigurations={fieldConfigs}
				/>
			</>
		);
	};

	const renderLimits = (updatedPeril: PerilUpdateRequest) => {
		return (
			<UpdatedPerilDetails
				perilObj={updatedPeril}
				id={`${updatedPeril.perilLocator}-${Math.random()}`}
				perilConfig={updatedPeril}
				isInEditMode={false}
				userHasWriteAccess={false}
				updateLimit={null}
				policyCharacteristic={policyCharacteristic}
				currency={
					policyData && policyData.policy ? policyData.policy.currency : null
				}
			/>
		);
	};

	const renderUpdatedPerilAccordions = () => {
		return updatedPerils.map((updatedPeril, ind) => {
			const peril = perils.find((p) => p.locator === updatedPeril.perilLocator);
			const perilConfig = exposureConfiguration.perils.find(
				(config) => config.name === peril.name,
			);
			const displayName = perilConfig.displayName;
			return (
				<Accordion key={`updatedPeril-${ind}`}>
					<AccordionSummary
						style={{ borderBottom: '1px solid rgba(0, 0, 0, .125)' }}
						expandIcon={<ExpandMoreIcon />}
					>{`Updated: ${displayName}`}</AccordionSummary>
					<AccordionDetails
						style={{ display: 'flex', flexDirection: 'column' }}
					>
						{renderFields(updatedPeril, peril, perilConfig)}
						{!hidePerilLimitsEnabled && renderLimits(updatedPeril)}
					</AccordionDetails>
				</Accordion>
			);
		});
	};

	return <>{renderUpdatedPerilAccordions()}</>;
};

const Break = styled.div`
	width: 100%;
	height: 0;
`;

export default withPolicyContext<Props>(UpdatedPerils);
